import {
  Component,
  Input,
  Injectable,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
  afterNextRender,
  HostListener,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ReviewGridCellComponent } from '@components/seoPageElements/reviewGrid/review-grid-cell/review-grid-cell.component';
import { isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Injectable({
  providedIn: 'root',
})
@Component({
  selector: 'app-review-grid',
  templateUrl: './reviewGrid.component.html',
  styleUrls: ['./reviewGrid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReviewGridCellComponent, MatIconModule],
})
export class ReviewGridComponent implements OnInit, OnDestroy {
  @ViewChild('gridWrapper') gridWrapperRef!: ElementRef<HTMLDivElement>;

  @Input() reviews!: any[];
  firstHalf: any[] = [];
  secondHalf: any[] = [];

  protected readonly showLeftArrow = signal(false);
  protected readonly showRightArrow = signal(false);
  private timeoutIds: ReturnType<typeof setTimeout>[] = [];

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    afterNextRender(() => {
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 100));
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 500));
    });
  }

  ngOnDestroy(): void {
    // Clear all timeouts to prevent memory leaks
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];
  }

  @HostListener('window:resize')
  onResize() {
    if (isPlatformBrowser(this.platformId)) {
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 100));
    }
  }

  ngOnInit(): void {
    const midpoint = Math.round(this.reviews.length / 2);
    const half1 = this.reviews.slice(0, midpoint);
    const half2 = this.reviews.slice(midpoint);

    // Sort both halves by message length
    half1.sort((a, b) => (b.message?.length || 0) - (a.message?.length || 0));
    half2.sort((a, b) => (b.message?.length || 0) - (a.message?.length || 0));

    // Assign the half with the longer first message to firstHalf
    if ((half1[0]?.message?.length || 0) >= (half2[0]?.message?.length || 0)) {
      this.firstHalf = half1;
      this.secondHalf = half2;
    } else {
      this.firstHalf = half2;
      this.secondHalf = half1;
    }
  }

  checkScrollArrows(): void {
    if (!isPlatformBrowser(this.platformId) || !this.gridWrapperRef) {
      return;
    }

    const container = this.gridWrapperRef.nativeElement;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Only proceed if the container has proper dimensions
    if (scrollWidth === 0 || clientWidth === 0) {
      // Retry after a short delay if dimensions aren't ready
      this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 50));
      return;
    }

    this.showLeftArrow.set(scrollLeft > 0);
    this.showRightArrow.set(scrollLeft < scrollWidth - clientWidth - 1);
  }

  scrollLeft(): void {
    if (!isPlatformBrowser(this.platformId) || !this.gridWrapperRef) {
      return;
    }

    const container = this.gridWrapperRef.nativeElement;
    const scrollAmount = 300; // Adjust scroll distance as needed
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });

    // Check arrows after scroll animation
    this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
  }

  scrollRight(): void {
    if (!isPlatformBrowser(this.platformId) || !this.gridWrapperRef) {
      return;
    }

    const container = this.gridWrapperRef.nativeElement;
    const scrollAmount = 300; // Adjust scroll distance as needed
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    // Check arrows after scroll animation
    this.timeoutIds.push(setTimeout(() => this.checkScrollArrows(), 300));
  }
}
